import { Button } from "@nimit9/signet-ui/components/button"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { DownloadIcon, FileTextIcon, InboxIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { ConnectClaudeDialog } from "@/components/common/connect-claude-dialog"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import {
  isShareExpired,
  purgeSharePayload,
  SHARE_CACHE,
  SHARE_FILE_KEY,
  SHARE_META_KEY,
  SHARE_TTL_MS,
  type ShareTargetMeta,
} from "@/lib/share-target"
import { useUIStore } from "@/store/ui"

export const Route = createFileRoute("/share-target")({
  component: ShareTarget,
})

type LoadState =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "routed" } // handed off to the CSV import dialog, navigating away
  | { status: "landed"; meta: ShareTargetMeta; file: File | null }

/** PDF / Excel statements are read on the device by the statement dialog (UAT J11-01). */
function isStatementFile(meta: ShareTargetMeta): boolean {
  const name = (meta.fileName ?? "").toLowerCase()
  return (
    meta.fileType === "application/pdf" ||
    /spreadsheet|ms-excel/.test(meta.fileType ?? "") ||
    /\.(pdf|xlsx?)$/.test(name)
  )
}

function isCsv(meta: ShareTargetMeta): boolean {
  if (meta.fileType === "text/csv") return true
  return (meta.fileName ?? "").toLowerCase().endsWith(".csv")
}

const consumeSharePayload = () => purgeSharePayload()

function ShareTarget() {
  const [state, setState] = useState<LoadState>({ status: "loading" })
  const [claudeOpen, setClaudeOpen] = useState(false)
  const navigate = useNavigate()
  const setSharedFile = useUIStore((s) => s.setSharedFile)
  const setPendingAction = useUIStore((s) => s.setPendingAction)

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!("caches" in window)) {
        if (!cancelled) setState({ status: "empty" })
        return
      }
      const cache = await caches.open(SHARE_CACHE)
      const metaRes = await cache.match(SHARE_META_KEY)
      if (!metaRes) {
        if (!cancelled) setState({ status: "empty" })
        return
      }
      const meta = (await metaRes.json()) as ShareTargetMeta
      if (isShareExpired(meta)) {
        // Past its TTL (or unstamped): purge rather than resurface it.
        await consumeSharePayload()
        if (!cancelled) setState({ status: "empty" })
        return
      }
      const fileRes = await cache.match(SHARE_FILE_KEY)
      const file = fileRes
        ? new File(
            [await fileRes.blob()],
            meta.fileName ?? (isCsv(meta) ? "shared.csv" : "shared"),
            {
              type: meta.fileType ?? (isCsv(meta) ? "text/csv" : "application/octet-stream"),
            },
          )
        : null

      if (cancelled) return

      if (file && isCsv(meta)) {
        // Handed straight to the import dialog — consume the payload so a
        // reload of this page doesn't replay the share.
        await consumeSharePayload()
        setSharedFile(file)
        setPendingAction("csv_import")
        setState({ status: "routed" })
        navigate({ to: "/transactions" })
        return
      }

      if (file && isStatementFile(meta)) {
        await consumeSharePayload()
        setSharedFile(file)
        setPendingAction("statement_import")
        setState({ status: "routed" })
        navigate({ to: "/transactions" })
        return
      }

      // Not a statement file: keep the payload cached until the person has chosen what to
      // do with it (they may want to save the file to hand to Claude after
      // connecting it) — consumed on Done, or when they leave this page.
      setState({ status: "landed", meta, file })
    }

    load()
    return () => {
      cancelled = true
    }
  }, [navigate, setSharedFile, setPendingAction])

  const landedAt = state.status === "landed" ? state.meta.createdAt : null
  useEffect(() => {
    if (landedAt === null) return
    // The TTL holds even while this page stays open.
    const timer = setTimeout(
      () => {
        void consumeSharePayload()
        setState({ status: "empty" })
      },
      Math.max(0, landedAt + SHARE_TTL_MS - Date.now()),
    )
    // Leaving the page counts as dismissing the share.
    return () => {
      clearTimeout(timer)
      void consumeSharePayload()
    }
  }, [landedAt])

  const landedFile = state.status === "landed" ? state.file : null
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!landedFile || typeof URL.createObjectURL !== "function") return
    const url = URL.createObjectURL(landedFile)
    setFileUrl(url)
    return () => {
      URL.revokeObjectURL(url)
      setFileUrl(null)
    }
  }, [landedFile])

  const done = useCallback(() => {
    void consumeSharePayload()
    navigate({ to: "/transactions" })
  }, [navigate])

  return (
    <>
      <Header title="Shared with Paisa" showPeriod={false} />
      <PageWrapper>
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-xl border border-border-subtle bg-bg-surface px-6 py-10 text-center">
          {state.status === "loading" || state.status === "routed" ? (
            <p className="text-body text-text-muted">Opening…</p>
          ) : state.status === "empty" ? (
            <>
              <InboxIcon className="h-8 w-8 text-text-hint" />
              <p className="text-callout font-medium text-text-primary">Nothing shared yet</p>
              <p className="text-caption text-text-muted">
                Share a statement into Paisa from another app to see it here.
              </p>
              <Button
                nativeButton={false}
                render={<Link to="/transactions" />}
                variant="outline"
                size="sm"
              >
                Go to Transactions
              </Button>
            </>
          ) : (
            <>
              <FileTextIcon className="h-8 w-8 text-accent-green" />
              <p className="text-callout font-medium text-text-primary">
                {state.file ? "Got it — that's not a CSV" : "That share isn't importable yet"}
              </p>
              <p className="text-caption text-text-muted">
                {state.file
                  ? `"${state.meta.fileName ?? "This file"}" is a statement Paisa can't parse in the browser. Claude can — connect it below and hand the file off there. Paisa keeps the file here until you're done.`
                  : "That share didn't include a file Paisa can import yet."}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="touch" onClick={() => setClaudeOpen(true)}>
                  Connect Claude
                </Button>
                {state.file && fileUrl && (
                  <Button
                    nativeButton={false}
                    render={
                      // biome-ignore lint/a11y/useAnchorContent: Button renders the label into this anchor
                      <a href={fileUrl} download={state.file.name} />
                    }
                    variant="outline"
                    size="touch"
                    className="gap-1.5"
                  >
                    <DownloadIcon className="size-4" />
                    Save file
                  </Button>
                )}
                <Button variant="outline" size="touch" onClick={done}>
                  Done
                </Button>
              </div>
            </>
          )}
        </div>
      </PageWrapper>
      <ConnectClaudeDialog
        open={claudeOpen}
        onClose={() => setClaudeOpen(false)}
        context="Statements shared into Paisa that aren't CSV import through Claude."
      />
    </>
  )
}
